const INVALID_FIELD_SELECTOR = '[data-validation-error="true"]'
const VALIDATION_FIELD_SELECTOR = '[data-validation-field]'

const observers = new WeakMap<HTMLElement, () => void>()

const isVisible = (element: HTMLElement) => {
  const rect = element.getBoundingClientRect()

  if (rect.width <= 0 || rect.height <= 0) {
    return false
  }

  let ancestor: HTMLElement | null = element
  while (ancestor && ancestor !== document.body) {
    const style = window.getComputedStyle(ancestor)
    const ancestorRect = ancestor.getBoundingClientRect()
    const clipsVertically = ['hidden', 'clip'].includes(style.overflowY)
    const clipsHorizontally = ['hidden', 'clip'].includes(style.overflowX)

    if (
      ancestor.hidden ||
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      (clipsVertically && ancestorRect.height <= 0) ||
      (clipsHorizontally && ancestorRect.width <= 0)
    ) {
      return false
    }

    if (
      (clipsVertically && (rect.bottom <= ancestorRect.top || rect.top >= ancestorRect.bottom)) ||
      (clipsHorizontally && (rect.right <= ancestorRect.left || rect.left >= ancestorRect.right))
    ) {
      return false
    }

    ancestor = ancestor.parentElement
  }

  return true
}

const getValidationScope = (trigger?: HTMLElement | null) => {
  if (!trigger) {
    return document.body
  }

  let container = trigger.parentElement
  while (container && container !== document.body) {
    if (container.querySelector(VALIDATION_FIELD_SELECTOR)) {
      return container
    }

    container = container.parentElement
  }

  return undefined
}

export const focusFirstInvalidField = (trigger?: HTMLElement | null) => {
  const scope = getValidationScope(trigger)
  const invalidField = Array.from(
    scope?.querySelectorAll<HTMLElement>(INVALID_FIELD_SELECTOR) || []
  ).find(isVisible)

  if (!invalidField) {
    return false
  }

  const focusTarget = invalidField.matches('input, textarea, select, button, [tabindex]')
    ? invalidField
    : invalidField.querySelector<HTMLElement>('input, textarea, select, button, [tabindex]')

  focusTarget?.focus({preventScroll: true})
  invalidField.scrollIntoView({
    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    block: 'center',
    inline: 'nearest',
  })

  return true
}

export const focusFirstInvalidFieldAfterValidation = (trigger?: HTMLElement | null) => {
  const scope = getValidationScope(trigger)

  if (!scope) {
    return
  }

  observers.get(scope)?.()

  let animationFrame:number | undefined
  let timeout:number | undefined
  const observer = new MutationObserver(() => {
    if (animationFrame !== undefined) {
      cancelAnimationFrame(animationFrame)
    }
    animationFrame = requestAnimationFrame(() => {
      if (focusFirstInvalidField(trigger)) {
        cleanup()
      }
    })
  })
  const cleanup = () => {
    observer.disconnect()
    if (animationFrame !== undefined) {
      cancelAnimationFrame(animationFrame)
    }
    if (timeout !== undefined) {
      window.clearTimeout(timeout)
    }
    observers.delete(scope)
  }

  observers.set(scope, cleanup)
  observer.observe(scope, {attributes: true, childList: true, subtree: true})
  timeout = window.setTimeout(cleanup, 30000)
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (focusFirstInvalidField(trigger)) {
        cleanup()
      }
    })
  })
}
